<script setup lang="ts">
import { ref, onMounted, onUnmounted } from 'vue'
import { hall } from '@/hall'
import { ActivityList, BINDACTIVITY } from '@/modules/BindActivityModule'
import { EventDefine } from '@/defines/EventDefine'

const emit = defineEmits(['clickAct', 'openMore'])

interface ActivityData {
  id: number
  type: number
  label: string
  icon: string
  badge: boolean
}

const localActivities = ref<ActivityData[]>([])

const getIconPath = (iconName: string) => {
  return new URL(`../assets/hall/bindActivity/${iconName}.webp`, import.meta.url).href
}

const getActivityNames = (id: number, stringList: Record<string, string>) => {
  const key = ActivityList[id]
  if (!key || typeof key !== 'string') {
    return 'Unknown Activity'
  }
  const text = stringList[key]
  if (!text) {
    return key
  }
  return text
}

const refreshActivities = () => {
  const info = hall.bindActivityModule.activityList || []
  const list: ActivityData[] = []
  
  const pannel = BINDACTIVITY

  for (let i = 0; i < info.length; i++) {
    const id = info[i] as unknown as number
    if (id === undefined) continue
    
    const key = ActivityList[id] as string
    const str = getActivityNames(id, pannel)
    
    let badge = false 
    if (key === 'LuckyWheel') badge = hall.bindActivityModule.turnTableRed
    else if (key === 'DailyCash') badge = hall.bindActivityModule.signRed
    else if (key === 'FreeSpinEveryday') badge = hall.bindActivityModule.freeSpinRed
    else if (key === 'Bonus') badge = hall.bindActivityModule.diamondRed
    else if (key === 'Rank') badge = hall.bindActivityModule.rankRed
    else if (typeof (hall.bindActivityModule as unknown as Record<string, unknown>)[`${key}Red`] !== 'undefined') {
      badge = !!(hall.bindActivityModule as unknown as Record<string, unknown>)[`${key}Red`]
    }

    list.push({
      id: id,
      type: id,
      label: str,
      icon: key,
      badge: badge
    })
  }

  localActivities.value = list
}

onMounted(() => {
  refreshActivities()
  if (localActivities.value.length === 0) {
    hall.bindActivityModule.changeRedStatus() 
  }
  
  hall.eventDispatcher.on(EventDefine.ChangeActivityRed, refreshActivities)
})

onUnmounted(() => {
  hall.eventDispatcher.off(EventDefine.ChangeActivityRed, refreshActivities)
})
</script>

<template>
  <section class="quick-actions">
    <!-- Show up to 6 items; items 4-6 are hidden on mobile via CSS -->
    <div
      v-for="(act, index) in localActivities.slice(0, 8)"
      :key="act.type"
      class="action-item cursor-pointer"
      :class="{ 'extra-item': index >= 3 }"
      @click="emit('clickAct', act)"
    >
      <div class="kuang-bg">
        <img src="@/assets/hall/kuang.png" class="kuang-img" alt="" />
        <div class="icon-wrap dynamic-icon">
          <img :src="getIconPath(act.icon)" class="act-img" />
          <span v-if="act.badge" class="badge"></span>
        </div>
      </div>
      <span class="action-label" v-html="act.label.replace(' ', '<br/>')"></span>
    </div>

    <!-- Placeholder items when < 3 activities loaded (mobile) -->
    <template v-if="localActivities.length < 3">
      <div v-for="i in 3 - localActivities.length" :key="'placeholder-' + i" class="action-item">
        <div class="kuang-bg">
          <img src="@/assets/hall/kuang.png" class="kuang-img" alt="" />
          <div class="icon-wrap loading-placeholder"></div>
        </div>
        <span class="action-label">Loading...</span>
      </div>
    </template>

    <!-- "More Events" button always last -->
    <div class="action-item cursor-pointer" @click="emit('openMore')">
      <div class="kuang-bg">
        <img src="@/assets/hall/kuang.png" class="kuang-img" alt="" />
        <div class="icon-wrap more-events-btn">
          <img src="@/assets/hall/down.png" class="more-icon" />
        </div>
      </div>
      <span class="action-label">More<br />Events</span>
    </div>
  </section>
</template>

<style scoped>
.quick-actions {
  display: flex;
  justify-content: space-around;
  padding: 15px 5px;
  flex-wrap: nowrap;
}

.action-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  text-align: center;
  color: #fff;
  /* Mobile: 4 per row (3 acts + More) */
  width: 25%;
  flex-shrink: 0;
}

/* Extra items (4th, 5th, 6th activity) hidden on mobile */
.extra-item {
  display: none;
}

.action-label {
  font-size: 0.8rem;
  line-height: 1.2;
}

.kuang-bg {
  position: relative;
  width: 72px;
  height: 72px;
  display: flex;
  justify-content: center;
  align-items: center;
}

.kuang-img {
  position: absolute;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  z-index: 1;
}

.icon-wrap {
  width: 52px;
  height: 52px;
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 2;
  border-radius: 12px;
}

.more-events-btn {
  color: var(--theme-accent);
}

.loading-placeholder {
  background: rgba(255, 255, 255, 0.1);
  animation: pulse 1.5s infinite;
}

@keyframes pulse {
  0% { opacity: 0.3; }
  50% { opacity: 0.7; }
  100% { opacity: 0.3; }
}

.more-icon {
  width: 40px;
  height: auto;
  object-fit: contain;
}

.act-img {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.badge {
  position: absolute;
  top: -2px;
  right: -2px;
  width: 12px;
  height: 12px;
  background: #ff3b30;
  border-radius: 50%;
  border: 1px solid var(--theme-nav-bg);
  z-index: 5;
}

/* ===== DESKTOP: show extra items, adjust sizing ===== */
@media (min-width: 768px) {
  .quick-actions {
    padding: 15px 10px;
    justify-content: flex-start;
    gap: 0;
  }

  /* Show all 6 activities on desktop */
  .extra-item {
    display: flex;
  }

  /* 9 items total (8 acts + More) → ~11% each */
  .action-item {
    width: calc(100% / 9);
    flex-shrink: 0;
  }

  .kuang-bg {
    width: 80px;
    height: 80px;
  }

  .icon-wrap {
    width: 58px;
    height: 58px;
  }
}
</style>
